import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

const PlayerContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)',
    borderRadius: '12px',
    padding: '12px 16px 12px 16px',
    paddingRight: '24px',
    border: '2px solid rgba(26, 115, 232, 0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
}));

const WaveBackground = styled(Box)(({ isPlaying }) => ({
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '100%',
    height: '80px',
    marginTop: '-40px',
    pointerEvents: 'none',
    overflow: 'hidden',
    '& svg': {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '200%',
        height: '100%',
    },
    '& .wave-1': {
        animation: isPlaying ? 'waveSlide1 5s linear infinite' : 'waveSlide1 12s linear infinite',
        '& path': {
            fill: 'rgba(239, 83, 80, 0.05)',
            stroke: 'rgba(239, 83, 80, 0.08)',
            strokeWidth: '3',
            filter: 'drop-shadow(0 0 6px rgba(239, 83, 80, 0.15))',
        },
    },
    '& .wave-2': {
        animation: isPlaying ? 'waveSlide2 6s linear infinite' : 'waveSlide2 14s linear infinite',
        '& path': {
            fill: 'rgba(26, 115, 232, 0.05)',
            stroke: 'rgba(26, 115, 232, 0.08)',
            strokeWidth: '2.5',
            filter: 'drop-shadow(0 0 6px rgba(26, 115, 232, 0.15))',
        },
    },
    '& .wave-3': {
        animation: isPlaying ? 'waveSlide3 7s linear infinite' : 'waveSlide3 16s linear infinite',
        '& path': {
            fill: 'rgba(0, 191, 165, 0.05)',
            stroke: 'rgba(0, 191, 165, 0.08)',
            strokeWidth: '2.5',
            filter: 'drop-shadow(0 0 6px rgba(0, 191, 165, 0.15))',
        },
    },
    '& .wave-4': {
        animation: isPlaying ? 'waveSlide4 8s linear infinite' : 'waveSlide4 18s linear infinite',
        '& path': {
            fill: 'rgba(33, 150, 243, 0.05)',
            stroke: 'rgba(33, 150, 243, 0.08)',
            strokeWidth: '2',
            filter: 'drop-shadow(0 0 6px rgba(33, 150, 243, 0.15))',
        },
    },
    '@keyframes waveSlide1': {
        '0%': {
            transform: 'translateX(0)',
        },
        '100%': {
            transform: 'translateX(-50%)',
        },
    },
    '@keyframes waveSlide2': {
        '0%': {
            transform: 'translateX(0)',
        },
        '100%': {
            transform: 'translateX(-50%)',
        },
    },
    '@keyframes waveSlide3': {
        '0%': {
            transform: 'translateX(-50%)',
        },
        '100%': {
            transform: 'translateX(0)',
        },
    },
    '@keyframes waveSlide4': {
        '0%': {
            transform: 'translateX(0)',
        },
        '100%': {
            transform: 'translateX(-50%)',
        },
    },
}));

const WaveOverlay = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    background: 'radial-gradient(ellipse at center, rgba(26, 115, 232, 0.03) 0%, transparent 70%)',
});

const PlayButton = styled(IconButton)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: '#ffffff',
    width: '40px',
    height: '40px',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
        transform: 'scale(1.05)',
    },
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
}));

const VolumeButton = styled(IconButton)(({ theme }) => ({
    color: '#1a73e8',
    width: '36px',
    height: '36px',
    '&:hover': {
        background: 'rgba(26, 115, 232, 0.1)',
    },
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
    color: '#1a73e8',
    height: 6,
    '& .MuiSlider-track': {
        background: 'linear-gradient(90deg, #1a73e8 0%, #00bfa5 100%)',
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        width: 16,
        height: 16,
        backgroundColor: '#ffffff',
        border: '3px solid #1a73e8',
        '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 8px rgba(26, 115, 232, 0.16)',
        },
    },
    '& .MuiSlider-rail': {
        backgroundColor: '#3a3a3a',
        opacity: 0.5,
    },
}));

const VolumeSlider = styled(Slider)(({ theme }) => ({
    color: '#1a73e8',
    height: 4,
    '& .MuiSlider-track': {
        background: 'linear-gradient(90deg, #1a73e8 0%, #00bfa5 100%)',
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        width: 12,
        height: 12,
        backgroundColor: '#ffffff',
        border: '2px solid #1a73e8',
        '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 6px rgba(26, 115, 232, 0.16)',
        },
    },
    '& .MuiSlider-rail': {
        backgroundColor: '#3a3a3a',
        opacity: 0.5,
    },
}));

const TimeText = styled(Typography)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#5f6368',
    minWidth: '80px',
    textAlign: 'center',
}));

const CustomAudioPlayer = ({ src }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const volumeTimeoutRef = useRef(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateTime = () => setCurrentTime(audio.currentTime);
        const updateDuration = () => setDuration(audio.duration);
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('ended', handleEnded);
        };
    }, []);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleSliderChange = (event, newValue) => {
        const audio = audioRef.current;
        audio.currentTime = newValue;
        setCurrentTime(newValue);
    };

    const handleVolumeChange = (event, newValue) => {
        const audio = audioRef.current;
        audio.volume = newValue;
        setVolume(newValue);
        if (newValue === 0) {
            setIsMuted(true);
        } else {
            setIsMuted(false);
        }
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (isMuted) {
            audio.volume = volume || 0.5;
            setVolume(volume || 0.5);
            setIsMuted(false);
        } else {
            audio.volume = 0;
            setIsMuted(true);
        }
    };

    const handleVolumeHover = () => {
        setShowVolumeSlider(true);
        if (volumeTimeoutRef.current) {
            clearTimeout(volumeTimeoutRef.current);
        }
    };

    const handleVolumeLeave = () => {
        volumeTimeoutRef.current = setTimeout(() => {
            setShowVolumeSlider(false);
        }, 1000);
    };

    useEffect(() => {
        return () => {
            if (volumeTimeoutRef.current) {
                clearTimeout(volumeTimeoutRef.current);
            }
        };
    }, []);

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <PlayerContainer>
            <audio ref={audioRef} src={src} preload="metadata" />

            {/* Multi-colored Wave Lines */}
            <WaveBackground isPlaying={isPlaying}>
                {/* Wave 1 - Red - Short wavelength */}
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="wave-1">
                    <path d="M0,40 Q60,0 120,40 T240,40 T360,40 T480,40 T600,40 T720,40 T840,40 T960,40 T1080,40 T1200,40 T1320,40 T1440,40" />
                </svg>

                {/* Wave 2 - Blue - Medium wavelength */}
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="wave-2">
                    <path d="M0,40 Q90,15 180,40 T360,40 T540,40 T720,40 T900,40 T1080,40 T1260,40 T1440,40" />
                </svg>

                {/* Wave 3 - Teal - Long wavelength */}
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="wave-3">
                    <path d="M0,40 Q120,5 240,40 T480,40 T720,40 T960,40 T1200,40 T1440,40" />
                </svg>

                {/* Wave 4 - Light Blue - Very short wavelength */}
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="wave-4">
                    <path d="M0,40 Q40,20 80,40 T160,40 T240,40 T320,40 T400,40 T480,40 T560,40 T640,40 T720,40 T800,40 T880,40 T960,40 T1040,40 T1120,40 T1200,40 T1280,40 T1360,40 T1440,40" />
                </svg>
            </WaveBackground>

            {/* Subtle Overlay */}
            <WaveOverlay />

            {/* Play/Pause Button */}
            <PlayButton onClick={togglePlay} sx={{ zIndex: 1 }}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </PlayButton>

            {/* Time Display */}
            <TimeText sx={{ zIndex: 1 }}>
                {formatTime(currentTime)} / {formatTime(duration)}
            </TimeText>

            {/* Progress Slider */}
            <Box sx={{ flex: 1, px: 1, zIndex: 1, display: 'flex', alignItems: 'center' }}>
                <StyledSlider
                    value={currentTime}
                    max={duration || 100}
                    onChange={handleSliderChange}
                    aria-label="audio progress"
                />
            </Box>

            {/* Volume Control */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1,
                    minWidth: showVolumeSlider ? '130px' : '44px',
                    transition: 'min-width 0.3s ease'
                }}
                onMouseEnter={handleVolumeHover}
                onMouseLeave={handleVolumeLeave}
            >
                <VolumeButton onClick={toggleMute} size="small">
                    {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </VolumeButton>

                <Box
                    sx={{
                        width: showVolumeSlider ? '80px' : '0px',
                        opacity: showVolumeSlider ? 1 : 0,
                        transition: 'all 0.3s ease',
                        overflow: 'visible',
                        display: 'flex',
                        alignItems: 'center',
                        pr: 1
                    }}
                >
                    <VolumeSlider
                        value={isMuted ? 0 : volume}
                        max={1}
                        step={0.1}
                        onChange={handleVolumeChange}
                        aria-label="volume"
                        size="small"
                    />
                </Box>
            </Box>
        </PlayerContainer>
    );
};

export default CustomAudioPlayer;
